import React, { useState, useCallback, useMemo } from 'react';
import { View, SlotInfo } from 'react-big-calendar';
import moment from 'moment';
import { PageTitle, PageTitleContainer } from '../../ui-components/utils/menu-items/page-title';
import { CalendarView } from './components/calendar-view';
import { AppointmentModal } from './components/appointment-modal';
import { useAppointments } from '../../lib/hooks/useAppointments';
import {
  Appointment,
  CreateAppointmentDto,
  UpdateAppointmentDto,
} from '../../lib/interfaces/appointment.interface';
import { Button } from '../../ui-components/ui/button';
import { Plus, Users, User, Filter } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui-components/ui/select';
import { Skeleton } from '../../ui-components/ui/skeleton';
import { useUser } from '../../lib/hooks/use-user';
import { UserRole } from '../../lib/interfaces/user';

type FilterMode = 'me' | 'team' | 'all';

export const CalendarPage: React.FC = () => {
  const { user } = useUser();
  const isAdmin = user?.role === UserRole.Admin || user?.role === UserRole.SuperAdmin;
  
  const [currentView, setCurrentView] = useState<View>('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filterMode, setFilterMode] = useState<FilterMode>('me');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [defaultSlot, setDefaultSlot] = useState<{ start: Date; end: Date } | null>(null);

  // Calculate date range for fetching appointments
  const dateRange = useMemo(() => {
    const start = moment(currentDate).startOf(currentView as any).toDate();
    const end = moment(currentDate).endOf(currentView as any).toDate();
    return { from: start, to: end };
  }, [currentDate, currentView]);
  
  const {
    appointments,
    loading,
    createAppointment,
    updateAppointment,
    deleteAppointment,
    markAsComplete,
  } = useAppointments({
    from: dateRange.from,
    to: dateRange.to,
    // In real implementation, filter by user based on filterMode
  });

  const handleSelectSlot = useCallback((slotInfo: SlotInfo) => {
    setSelectedAppointment(null);
    setDefaultSlot({ start: slotInfo.start, end: slotInfo.end });
    setModalOpen(true);
  }, []);

  const handleSelectEvent = useCallback((appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setDefaultSlot(null);
    setModalOpen(true);
  }, []);

  const handleSave = async (data: CreateAppointmentDto | UpdateAppointmentDto, id?: string) => {
    if (id) {
      await updateAppointment(id, data as UpdateAppointmentDto);
    } else {
      await createAppointment(data as CreateAppointmentDto);
    }
    setModalOpen(false);
  };

  const handleDelete = async (id: string) => {
    await deleteAppointment(id);
    setModalOpen(false);
  };

  const handleMarkComplete = async (id: string, notes?: string) => {
    await markAsComplete(id, notes);
    setModalOpen(false);
  };

  const handleEventDrop = async ({ event, start, end }: any) => {
    const appointment = event.resource as Appointment;
    await updateAppointment(appointment._id, {
      start: start.toISOString(),
      end: end.toISOString(),
    });
  };

  const handleEventResize = async ({ event, start, end }: any) => {
    const appointment = event.resource as Appointment;
    await updateAppointment(appointment._id, {
      start: start.toISOString(),
      end: end.toISOString(),
    });
  };

  const handleNavigate = (date: Date) => {
    setCurrentDate(date);
  };

  const handleViewChange = (view: View) => {
    setCurrentView(view);
  };

  return (
    <div>
      <PageTitleContainer>
        <div className="flex flex-row justify-between items-center">
          <PageTitle>Kalender</PageTitle>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Select value={filterMode} onValueChange={(value) => setFilterMode(value as FilterMode)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="me">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Meine Termine
                    </div>
                  </SelectItem>
                  <SelectItem value="team">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Team
                    </div>
                  </SelectItem>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      Alle
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button
              onClick={() => {
                setSelectedAppointment(null);
                setDefaultSlot(null);
                setModalOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Neuer Termin
            </Button>
          </div>
        </div>
      </PageTitleContainer>

      <div className="h-[calc(100vh-200px)] mt-6">
        <div className="bg-white rounded-lg shadow-sm border h-full">
          {loading && appointments.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-500">Termine werden geladen...</p>
              </div>
            </div>
          ) : (
            <CalendarView
              appointments={appointments}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              onEventDrop={handleEventDrop}
              onEventResize={handleEventResize}
              currentView={currentView}
              onViewChange={handleViewChange}
              currentDate={currentDate}
              onNavigate={handleNavigate}
            />
          )}
        </div>
      </div>

      <AppointmentModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedAppointment(null);
          setDefaultSlot(null);
        }}
        appointment={selectedAppointment}
        defaultStart={defaultSlot?.start}
        defaultEnd={defaultSlot?.end}
        onSave={handleSave}
        onDelete={handleDelete}
        onMarkComplete={handleMarkComplete}
      />
    </div>
  );
};
